import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useUrlState } from "@/lib/useUrlState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import { Bell, Plus, Trash, PencilSimple, Lock, Clock, Check } from "@phosphor-icons/react";
import { useNavCounts } from "@/contexts/NavCountsContext";

const TABS = [
  ["all", "All"],
  ["today", "Today"],
  ["overdue", "Overdue"],
  ["done", "Completed"],
];

// After the user taps the circle we hold the visual "completed" state for
// ~1.8s so the satisfying animation plays before the row leaves the list.
const COMPLETE_HOLD_MS = 1800;

export default function Reminders() {
  const confirm = useConfirm();
  const { refresh: refreshNavCounts, adjust: adjustNavCounts } = useNavCounts();
  const [tab, setTab] = useUrlState("tab", "all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  // ids that have been visually completed but not yet refreshed away
  const [transitioning, setTransitioning] = useState(() => new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reminders", { params: { tab } });
      setRows(data?.rows || []);
      refreshNavCounts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load reminders");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const toggle = async (r) => {
    const nextDone = !r.done;
    // Optimistic UI: flip the row visually right away. Then persist and reload
    // after a short delay so the animation can breathe.
    setRows((prev) => prev.map((x) => x.reminder_id === r.reminder_id ? { ...x, done: nextDone } : x));
    // Sidebar badge should tick down instantly too — user asked for zero-flash.
    adjustNavCounts({ reminders: nextDone ? -1 : +1 });
    setTransitioning((prev) => {
      const next = new Set(prev);
      next.add(r.reminder_id);
      return next;
    });
    try {
      await api.patch(`/reminders/${r.reminder_id}`, { done: nextDone });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      // Roll back the optimistic badge tick since the write failed.
      adjustNavCounts({ reminders: nextDone ? +1 : -1 });
      setTransitioning((prev) => {
        const next = new Set(prev);
        next.delete(r.reminder_id);
        return next;
      });
      load();
      return;
    }
    window.setTimeout(() => {
      setTransitioning((prev) => {
        const next = new Set(prev);
        next.delete(r.reminder_id);
        return next;
      });
      load();
    }, COMPLETE_HOLD_MS);
  };

  const remove = async (r) => {
    const ok = await confirm({ title: "Delete reminder?", message: "This action cannot be undone.", tone: "danger", confirmLabel: "Delete" });
    if (!ok) return;
    try {
      await api.delete(`/reminders/${r.reminder_id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-5" data-testid="reminders-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <Bell size={22} className="text-violet-600" />
            My Reminders
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Personal notes that only you can see.</p>
        </div>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="new-reminder-btn">
          <Plus size={14} className="mr-1.5" /> New reminder
        </Button>
      </div>

      {/* Privacy banner (the comforting one) */}
      <div className="rounded-md border border-violet-200 bg-violet-50 px-4 py-3 flex items-start gap-3" data-testid="privacy-banner">
        <Lock size={18} className="text-violet-700 shrink-0 mt-0.5" weight="fill" />
        <div>
          <div className="text-sm font-semibold text-violet-900">Private to you only</div>
          <div className="text-xs text-violet-800 mt-0.5">
            No admin, super-admin, or other teammate can see your reminders. Use them as your personal scratchpad.
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex items-center gap-1">
          {TABS.map(([v, label]) => (
            <button
              key={v}
              onClick={() => setTab(v)}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${tab === v ? "border-violet-600 text-violet-700" : "border-transparent text-gray-500 hover:text-gray-700"}`}
              data-testid={`reminders-tab-${v}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-md border border-gray-200 bg-white overflow-hidden">
        {loading ? (
          <div className="px-4 py-10 text-center text-sm text-gray-400">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-sm text-gray-400">No reminders here.</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {rows.map((r) => {
              const isLeaving = transitioning.has(r.reminder_id) && r.done && tab !== "done";
              return (
                <li
                  key={r.reminder_id}
                  className={`px-4 py-3 flex items-start gap-3 hover:bg-gray-50 transition-all duration-500 ${isLeaving ? "opacity-0 -translate-x-2" : "opacity-100 translate-x-0"}`}
                  data-testid={`reminder-row-${r.reminder_id}`}
                >
                  <ReminderCircle
                    done={!!r.done}
                    onToggle={() => toggle(r)}
                    testId={`reminder-done-${r.reminder_id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${r.done ? "line-through text-gray-400" : "text-gray-900"}`}>
                      {r.title}
                    </div>
                    {r.note ? (
                      <div className={`text-xs mt-0.5 ${r.done ? "text-gray-400" : "text-gray-600"} whitespace-pre-line`}>
                        {r.note}
                      </div>
                    ) : null}
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
                      <Clock size={11} />
                      <span>{r.date}{r.time ? ` · ${r.time}` : ""}</span>
                    </div>
                  </div>
                  <div className="shrink-0 flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => { setEditing(r); setShowForm(true); }} className="h-7 w-7 p-0" data-testid={`edit-reminder-${r.reminder_id}`}>
                      <PencilSimple size={12} />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(r)} className="h-7 w-7 p-0 text-rose-600 hover:bg-rose-50" data-testid={`delete-reminder-${r.reminder_id}`}>
                      <Trash size={12} />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <ReminderFormDialog
        open={showForm}
        editing={editing}
        onClose={() => { setShowForm(false); setEditing(null); }}
        onSaved={() => { setShowForm(false); setEditing(null); load(); }}
      />
    </div>
  );
}

/**
 * iOS-Reminders style circle. Empty outline by default; fills with violet and
 * shows a check on completion. Tappable.
 */
function ReminderCircle({ done, onToggle, testId }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={done}
      data-testid={testId}
      className={`mt-0.5 group relative w-[22px] h-[22px] rounded-full flex items-center justify-center transition-all duration-300 outline-none focus-visible:ring-2 focus-visible:ring-violet-300 focus-visible:ring-offset-1 ${
        done
          ? "bg-violet-600 border border-violet-600 scale-100"
          : "bg-white border-[1.5px] border-gray-300 hover:border-violet-400 hover:scale-105 active:scale-95"
      }`}
    >
      <Check
        size={12}
        weight="bold"
        className={`text-white transition-all duration-300 ${done ? "opacity-100 scale-100" : "opacity-0 scale-50"}`}
      />
      {done ? (
        <span className="absolute inset-0 rounded-full bg-violet-400/40 animate-ping" aria-hidden />
      ) : null}
    </button>
  );
}

function ReminderFormDialog({ open, editing, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [note, setNote] = useState("");
  const [alertMode, setAlertMode] = useState("none");
  const [alertDate, setAlertDate] = useState("");
  const [alertTime, setAlertTime] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTitle(editing.title || "");
      setDate(editing.date || "");
      setTime(editing.time || "");
      setNote(editing.note || "");
      setAlertMode(editing.alert_mode || "none");
      // For 'custom' we don't get original input back — leave fields blank so
      // the user re-enters if they want to change. alert_at is stored UTC.
      setAlertDate("");
      setAlertTime("");
    } else {
      setTitle(""); setDate(new Date().toISOString().slice(0, 10)); setTime(""); setNote("");
      setAlertMode("none"); setAlertDate(""); setAlertTime("");
    }
  }, [open, editing]);

  const valid = title.trim().length > 0 && !!date;

  const save = async () => {
    if (!valid) return;
    setBusy(true);
    try {
      const payload = {
        title: title.trim(),
        date,
        time: time || null,
        note: note.trim(),
        alert_mode: alertMode,
        alert_date: alertMode === "custom" ? (alertDate || null) : null,
        alert_time: alertMode === "custom" ? (alertTime || null) : null,
      };
      if (editing) {
        await api.patch(`/reminders/${editing.reminder_id}`, payload);
        toast.success("Reminder updated");
      } else {
        await api.post("/reminders", payload);
        toast.success("Reminder added");
      }
      // If the user enabled an alert AND the browser has not yet been asked,
      // prompt now — contextual, only when they actually want it.
      if (alertMode !== "none"
          && typeof Notification !== "undefined"
          && Notification.permission === "default") {
        try { await Notification.requestPermission(); } catch (_e) { /* ignore */ }
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="reminder-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit reminder" : "New reminder"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Call my CA on Tuesday" className="mt-1" data-testid="reminder-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1" data-testid="reminder-date" />
            </div>
            <div>
              <Label>Time (optional)</Label>
              <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1" data-testid="reminder-time" />
            </div>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Any details for your future self…" className="mt-1" data-testid="reminder-note" />
          </div>

          {/* Alert me at section */}
          <div className="rounded-md border border-gray-200 bg-gray-50 p-3 space-y-2">
            <Label className="text-xs font-semibold text-gray-700">Alert me at (optional)</Label>
            <div className="flex flex-col gap-1.5 text-sm">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="alert-mode-none">
                <input type="radio" name="alert_mode" checked={alertMode === "none"} onChange={() => setAlertMode("none")} className="accent-violet-600" />
                <span>No alert</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="alert-mode-same">
                <input type="radio" name="alert_mode" checked={alertMode === "same"} onChange={() => setAlertMode("same")} className="accent-violet-600" />
                <span>Same as reminder date / time</span>
                <span className="text-[10px] text-gray-400">(defaults to 09:00 IST if no time)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer" data-testid="alert-mode-custom">
                <input type="radio" name="alert_mode" checked={alertMode === "custom"} onChange={() => setAlertMode("custom")} className="accent-violet-600" />
                <span>Custom date / time</span>
              </label>
              {alertMode === "custom" ? (
                <div className="grid grid-cols-2 gap-3 pt-1 pl-6">
                  <div>
                    <Label className="text-[11px] text-gray-600">Alert date</Label>
                    <Input type="date" value={alertDate} onChange={(e) => setAlertDate(e.target.value)} className="mt-1 h-8 text-sm" data-testid="alert-date" />
                  </div>
                  <div>
                    <Label className="text-[11px] text-gray-600">Alert time</Label>
                    <Input type="time" value={alertTime} onChange={(e) => setAlertTime(e.target.value)} className="mt-1 h-8 text-sm" data-testid="alert-time" />
                  </div>
                </div>
              ) : null}
            </div>
            <div className="text-[10px] text-gray-500 mt-1">
              Browser desktop notification fires while Pulse is open in a tab.
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={!valid || busy} className="bg-violet-600 text-white hover:bg-violet-700" data-testid="reminder-save-btn">
            {editing ? "Save" : "Add reminder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
